import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";

type Status = "loading" | "confirm" | "success" | "already" | "error";

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<Status>("loading");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      return;
    }

    const validate = async () => {
      try {
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/unsubscribe-newsletter?token=${encodeURIComponent(token)}`;
        const res = await fetch(url, {
          headers: {
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
        });
        const result = await res.json();

        if (!res.ok || !result.valid) {
          setStatus("error");
          return;
        }

        setMaskedEmail(result.email);
        setStatus(result.alreadyUnsubscribed ? "already" : "confirm");
      } catch {
        setStatus("error");
      }
    };

    validate();
  }, [token]);

  const handleUnsubscribe = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/unsubscribe-newsletter`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();

      if (data?.success) {
        setStatus(data.alreadyUnsubscribed ? "already" : "success");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="Unsubscribe | Rob Lasa"
        description="Manage your email preferences."
      />
      <Navigation />
      <main className="flex-1 flex items-center justify-center px-4 py-20">
        <div className="max-w-md w-full text-center space-y-6">
          {status === "loading" && (
            <div className="flex justify-center">
              <div className="w-8 h-8 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
            </div>
          )}

          {status === "confirm" && (
            <>
              <h1 className="font-display text-2xl text-foreground">Unsubscribe</h1>
              <p className="text-foreground/70">
                Are you sure you want to unsubscribe{" "}
                <span className="font-medium text-foreground">{maskedEmail}</span>{" "}
                from Rob Lasa newsletters?
              </p>
              <button
                onClick={handleUnsubscribe}
                disabled={submitting}
                className="bg-accent text-white px-8 py-3 rounded font-sans text-sm tracking-wide hover:bg-accent/90 transition-colors disabled:opacity-50"
              >
                {submitting ? "Processing..." : "Yes, Unsubscribe"}
              </button>
              <p className="text-muted-foreground text-xs italic">
                You can always rejoin at souldadrob.com
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <h1 className="font-display text-2xl text-foreground">You've been unsubscribed</h1>
              <p className="text-foreground/70">
                You won't receive any more newsletters. No hard feelings.
              </p>
            </>
          )}

          {status === "already" && (
            <>
              <h1 className="font-display text-2xl text-foreground">Already unsubscribed</h1>
              <p className="text-foreground/70">
                <span className="font-medium text-foreground">{maskedEmail}</span>{" "}
                is already unsubscribed.
              </p>
            </>
          )}

          {status === "error" && (
            <>
              <h1 className="font-display text-2xl text-foreground">Invalid link</h1>
              <p className="text-foreground/70">
                This unsubscribe link is invalid or has expired. If you need help,
                reach out to{" "}
                <a href="mailto:rob@souldadrob.com" className="text-accent hover:underline">
                  rob@souldadrob.com
                </a>
              </p>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Unsubscribe;
